import { useEffect, useState } from 'react'
import { onlyDigits } from '@shared/lib/validation/documents'
import { lookupBrazilPostalCode } from './br-postal-code'
import type { BrazilPostalCodeAddress } from './br-postal-code'

export type CepLookupStatus = 'idle' | 'loading' | 'filled' | 'error'

export function useCepLookup(
  postalCode: string,
  options: { enabled?: boolean; delayMs?: number } = {},
): { address: BrazilPostalCodeAddress | null; status: CepLookupStatus } {
  const { enabled = true, delayMs = 350 } = options
  const normalizedPostalCode = onlyDigits(postalCode)
  const canLookup = enabled && normalizedPostalCode.length === 8
  const [result, setResult] = useState<{
    address: BrazilPostalCodeAddress | null
    postalCode: string
    status: CepLookupStatus
  }>({
    address: null,
    postalCode: '',
    status: 'idle',
  })

  useEffect(() => {
    if (!canLookup) {
      return undefined
    }

    const controller = new AbortController()
    const timeoutId = window.setTimeout(() => {
      setResult({ address: null, postalCode: normalizedPostalCode, status: 'loading' })
      void lookupBrazilPostalCode(normalizedPostalCode, controller.signal)
        .then((nextAddress) => {
          setResult({
            address: nextAddress,
            postalCode: normalizedPostalCode,
            status: nextAddress ? 'filled' : 'idle',
          })
        })
        .catch((error) => {
          if (!(error instanceof DOMException && error.name === 'AbortError')) {
            setResult({ address: null, postalCode: normalizedPostalCode, status: 'error' })
          }
        })
    }, delayMs)

    return () => {
      controller.abort()
      window.clearTimeout(timeoutId)
    }
  }, [canLookup, delayMs, normalizedPostalCode])

  if (!canLookup || result.postalCode !== normalizedPostalCode) {
    return { address: null, status: 'idle' }
  }

  return { address: result.address, status: result.status }
}
